import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icone, SqueletteListe, SquelettePage } from '../ui'
import {
  candidatsRecommandes,
  declarationAnnonce,
  enregistrerDeclarationAnnonce,
  expliquerAnnonce,
  traits as chargerTraits,
  type Candidat,
  type DeclarationAnnonce,
  type Explication,
  type Trait,
} from '../apiMatching'
import { ExplicationScore, MotifElimination } from './Explication'
import { BOOLEENS } from './Lifestyle'
import { ErreurApi } from '../api'
import { TEXTES, messageErreur, type Langue } from '../i18n'

/**
 * Ce que l'annonce déclare de son mode de vie — conception L5, §1.2.
 *
 * **Sans cette déclaration, l'axe lifestyle — 20 %, le seul que les concurrents n'ont pas — rend
 * une valeur neutre pour tout le monde.** Une annonce ne portait jusqu'ici que sa mixité, qui est
 * déjà un filtre éliminatoire et ne rapporte donc rien à l'axe pondéré.
 *
 * **Facultative, et l'absence neutralise au lieu de pénaliser.** La rendre pénalisante la rendrait
 * obligatoire de fait, et remplirait le catalogue de déclarations posées au hasard pour ne pas
 * perdre de rang. Le texte d'aide le dit à l'auteur : c'est ce qui évite qu'il coche au hasard.
 *
 * Les traits proposés sont ceux que le référentiel marque `appliesToListing` — cinq à sept, ceux
 * où l'auteur d'une annonce sait répondre. On ne demande pas son rythme de sommeil à un
 * propriétaire.
 */
export function ModeDeVieAnnonce({ langue, annonceId }: { langue: Langue; annonceId: string }) {
  const t = TEXTES[langue]

  const [items, setItems] = useState<Trait[] | null>(null)
  const [valeurs, setValeurs] = useState<Record<string, DeclarationAnnonce>>({})
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState(false)
  const [enCours, setEnCours] = useState(false)

  const charger = useCallback(async () => {
    try {
      const [liste, actuelles] = await Promise.all([
        chargerTraits(langue, true),
        declarationAnnonce(annonceId),
      ])

      setItems(liste)
      setValeurs(Object.fromEntries(actuelles.map((d) => [d.traitCode, d])))
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [annonceId, langue])

  useEffect(() => {
    void charger()
  }, [charger])

  const modifier = (code: string, champ: keyof DeclarationAnnonce, valeur: number | null) => {
    setSucces(false)
    setValeurs((actuel) => ({
      ...actuel,
      [code]: {
        traitCode: code,
        practiceValue: actuel[code]?.practiceValue ?? null,
        toleranceValue: actuel[code]?.toleranceValue ?? null,
        [champ]: valeur,
      } as DeclarationAnnonce,
    }))
  }

  const enregistrer = async (evenement: React.FormEvent) => {
    evenement.preventDefault()
    setErreur(null)
    setSucces(false)
    setEnCours(true)

    try {
      await enregistrerDeclarationAnnonce(
        annonceId,
        Object.values(valeurs).filter(
          (d) => d.practiceValue !== null || d.toleranceValue !== null,
        ),
      )

      setSucces(true)
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnCours(false)
    }
  }

  if (items === null) {
    return <SquelettePage />
  }

  return (
    <form className="mode-de-vie" onSubmit={enregistrer}>
      <h3>{t.modeDeVieAnnonce}</h3>
      <p className="aide">{t.modeDeVieAnnonceAide}</p>

      {erreur !== null && <p className="erreur">{erreur}</p>}
      {succes && <p className="succes">{t.lifestyleEnregistre}</p>}

      <ul className="traits">
        {items.map((trait) => (
          <li key={trait.code} className="trait">
            <p className="trait__titre">{trait.label}</p>

            <div className="trait__cotes">
              <label className="champ">
                <span>{t.cequisepratique}</span>
                <Crans
                  trait={trait}
                  valeur={valeurs[trait.code]?.practiceValue ?? null}
                  onChange={(v) => modifier(trait.code, 'practiceValue', v)}
                  langue={langue}
                />
              </label>

              <label className="champ">
                <span>{t.cequonaccepte}</span>
                <Crans
                  trait={trait}
                  valeur={valeurs[trait.code]?.toleranceValue ?? null}
                  onChange={(v) => modifier(trait.code, 'toleranceValue', v)}
                  langue={langue}
                />
              </label>
            </div>
          </li>
        ))}
      </ul>

      <button type="submit" className="bouton-principal" disabled={enCours}>
        {t.enregistrer}
      </button>
    </form>
  )
}

/**
 * Les candidats recommandés — **la symétrie du §7.3**.
 *
 * « Le propriétaire et le foyer voient leur propre classement des candidats, avec les mêmes
 * explications. » C'est la même table lue depuis l'autre extrémité, et elle n'est complète que
 * parce que la passe écrit l'union de deux top-K.
 *
 * Un candidat qui a déjà postulé est signalé : c'est un dossier à traiter, pas une piste à
 * solliciter, et les confondre ferait écrire deux fois à la même personne.
 */
export function CandidatsRecommandes({ langue, annonceId }: { langue: Langue; annonceId: string }) {
  const t = TEXTES[langue]

  const [candidats, setCandidats] = useState<Candidat[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    const charger = async () => {
      try {
        setCandidats((await candidatsRecommandes(annonceId)).items)
      } catch (cause) {
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      }
    }

    void charger()
  }, [annonceId, langue])

  if (erreur !== null) {
    return <p className="erreur">{erreur}</p>
  }

  if (candidats === null) {
    return <SquelettePage />
  }

  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })

  return (
    <div className="candidats">
      <h3>{t.candidatsRecommandes}</h3>

      {candidats.length === 0 && <p className="socle">{t.recommandationsJamaisCalculees}</p>}

      <ul className="vignettes">
        {candidats.map((candidat) => (
          <li key={candidat.id} className="vignette recommandation">
            <p className="recommandation__score">{pourcent.format(candidat.score)}</p>
            <p className="recommandation__titre">{candidat.seekerDisplayName}</p>
            {candidat.hasApplied && <p className="recommandation__meta">{t.aDejaCandidate}</p>}
            <ExplicationScore axes={candidat.axes} langue={langue} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function Crans({
  trait,
  valeur,
  onChange,
  langue,
}: {
  trait: Trait
  valeur: number | null
  onChange: (valeur: number | null) => void
  langue: Langue
}) {
  const t = TEXTES[langue]
  const crans = trait.scale === 'Boolean' ? [0, 1] : [0, 1, 2, 3, 4]
  // La table vient de Lifestyle.tsx : recopiee ici, elle avait deja oublie l'anglais le jour
  // ou il est arrive. Une seule table, un seul endroit ou l'ajouter.
  const booleens = BOOLEENS[langue]

  return (
    <select
      value={valeur === null ? '' : String(valeur)}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    >
      <option value="">{t.nonRenseigne}</option>
      {crans.map((cran) => (
        <option key={cran} value={cran}>
          {trait.scale === 'Boolean' ? (cran === 0 ? booleens.non : booleens.oui) : String(cran)}
        </option>
      ))}
    </select>
  )
}

/**
 * Le seuil à partir duquel une annonce est dite recommandée — ticket `fo-annonce-06`.
 *
 * **Un seuil d'AFFICHAGE, pas de calcul.** Le moteur ne connaît pas cette valeur : il classe, il ne
 * tranche pas, et la passe nocturne retient un top-K sans plancher (conception L5, §1.4). Ce 80 %
 * ne décide donc rien à part le libellé du panneau et son état d'ouverture — c'est le seul endroit
 * de la V1 où l'interface doit répondre par oui ou par non à une question que le score pose en
 * nuances.
 *
 * Le mettre en base avec les pondérations (invariant 10) serait cohérent le jour où il changerait
 * la sélection. Tant qu'il ne change qu'un intitulé, une constante lisible vaut mieux qu'une
 * lecture réseau de plus sur la fiche.
 */
const SEUIL_RECOMMANDEE = 0.8

/**
 * Comment cette annonce se situe pour moi — conception L5, §1.7.
 *
 * **Le seul chemin où un résultat éliminé a un lecteur.** Les éliminés ne sont pas persistés — une
 * ligne par couple serait à 95 % du déchet — donc ce panneau recalcule le couple à la volée, sans
 * rien écrire.
 *
 * Un éliminé n'a pas un score faible : il n'a pas de score (spec §7.3). L'écran affiche donc le
 * motif et **aucun pourcentage** — un « 0 % » laisserait croire à un mauvais classement là où il y
 * a un refus net.
 *
 * **Le libellé suit le verdict, et l'ouverture avec** — ticket `fo-annonce-06`, réouverture.
 *
 * Le titre disait « Pourquoi cette annonce ne m'est pas recommandée » et affichait ensuite 89 % sur
 * une annonce qui est, en base, la première recommandation de la personne. Un intitulé qui affirme
 * ce que son contenu contredit est pire qu'un intitulé vague : il fait douter du calcul plutôt que
 * du texte. Il ne peut donc pas être écrit à l'avance — il se déduit du résultat.
 *
 * Au-dessus du seuil le panneau s'ouvre : la réponse est bonne, elle se lit sans la demander. En
 * dessous il reste fermé : c'est une question qu'on se pose parfois, pas une explication qu'on
 * doit subir sur chaque fiche.
 *
 * **Le chargement est différé.** Cet appel recalcule un couple à la volée ; le faire pendant que
 * la fiche s'affiche le met en concurrence avec les photos et la carte, pour un panneau qui vit
 * sous la ligne de flottaison. Il part une fois la page peinte.
 */
export function PourquoiPas({ langue, annonceId }: { langue: Langue; annonceId: string }) {
  const t = TEXTES[langue]

  const [explication, setExplication] = useState<Explication | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  /** `null` tant que personne n'a cliqué : c'est le score qui décide alors de l'état. */
  const [bascule, setBascule] = useState<boolean | null>(null)

  useEffect(() => {
    let annule = false

    setChargement(true)
    setErreur(null)
    setExplication(null)

    // Un `setTimeout` et non `requestIdleCallback` : ce dernier n'existe pas sur Safari iOS, qui
    // est une part majoritaire du parc marocain. Le rendez-vous après peinture suffit — ce qu'on
    // cherche est de ne pas concurrencer les photos, pas de mesurer l'oisiveté.
    const jeton = globalThis.setTimeout(() => {
      void expliquerAnnonce(annonceId)
        .then((resultat) => {
          if (!annule) {
            setExplication(resultat)
          }
        })
        .catch((cause: unknown) => {
          // Dossier incomplet, ou aucune version de ponderations : ce n'est pas une panne, c'est
          // une question a laquelle on ne peut pas encore repondre.
          if (!annule) {
            setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
          }
        })
        .finally(() => {
          if (!annule) {
            setChargement(false)
          }
        })
    }, 300)

    return () => {
      annule = true
      globalThis.clearTimeout(jeton)
    }
  }, [annonceId, langue])

  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })

  const recommandee =
    explication !== null && !explication.isEliminated && explication.score >= SEUIL_RECOMMANDEE

  const ouvert = bascule ?? recommandee

  // Tant que la reponse n'est pas arrivee, le titre ne l'annonce pas : le neutre est le seul qui
  // ne devienne pas faux une seconde plus tard.
  const titre =
    explication === null
      ? t.annonceExpliquer
      : recommandee
        ? t.annonceExpliquerRecommandee
        : t.annonceExpliquerNonRecommandee

  return (
    <section className="fiche__section pourquoi-pas">
      {/* Le renvoi vers le fil complet, à droite du titre : c'est la place que lui donne la
          maquette, et la question « et les autres ? » se pose en lisant celle-ci. */}
      <div className="pourquoi-pas__entete">
        <h3>
          <button
            type="button"
            className="pourquoi-pas__bascule"
            aria-expanded={ouvert}
            onClick={() => setBascule(!ouvert)}
          >
            <Icone nom="chevron" taille="sm" miroir />
            {titre}
          </button>
        </h3>
        <Link to="/recommandations" className="pourquoi-pas__toutes">
          {t.annonceVoirMesRecommandations}
        </Link>
      </div>

      {ouvert &&
        (erreur !== null ? (
          <p className="socle">{erreur}</p>
        ) : chargement || explication === null ? (
          <SqueletteListe />
        ) : explication.isEliminated ? (
          <>
            <p className="pourquoi-pas__verdict">{t.annonceNonProposee}</p>
            <MotifElimination code={explication.eliminationRuleCode ?? ''} langue={langue} />
          </>
        ) : (
          <>
            {/* Le verdict AVANT le score. Un pourcentage nu se lit comme une note, alors qu'il ne
                répond qu'à la moitié de la question : 89 % ne dit pas si l'annonce remonte. */}
            <p className="pourquoi-pas__verdict">{t.annonceProposee}</p>
            <p className="recommandation__score">{pourcent.format(explication.score)}</p>
            <ExplicationScore axes={explication.axes} langue={langue} />
          </>
        ))}
    </section>
  )
}
